import { NavigationSection, SectionKey } from "../components/layout/AppSidebar";

export const brandPrimary = getComputedStyle(document.body).getPropertyValue('--alida-primary') || '#677ab1';
export const brandDark = getComputedStyle(document.body).getPropertyValue('--alida-primary-dark') || '#3f4e75';
export const brandSecondary = getComputedStyle(document.body).getPropertyValue('--alida-secondary') || '#9aa6d2';
export const datasourcePrimary = getComputedStyle(document.body).getPropertyValue('--datasource-color') || '#2dd4bf';
export const datasourceDark = getComputedStyle(document.body).getPropertyValue('--datasource-color-dark') || '#0d9488';
export const modelPrimary = getComputedStyle(document.body).getPropertyValue('--model-color') || '#2dd4bf';
export const modelDark = getComputedStyle(document.body).getPropertyValue('--model-color-dark') || '#0d9488';
export const datasetPrimary = getComputedStyle(document.body).getPropertyValue('--dataset-color') || '#2dd4bf';
export const datasetDark = getComputedStyle(document.body).getPropertyValue('--dataset-color-dark') || '#0d9488';
export const batchPrimary = getComputedStyle(document.body).getPropertyValue('--batch-color') || '#64748b';
export const batchDark = getComputedStyle(document.body).getPropertyValue('--batch-color-dark') || '#3b4553';
export const sourcePrimary = getComputedStyle(document.body).getPropertyValue('--source-color') || '#77ce26';
export const sourceDark = getComputedStyle(document.body).getPropertyValue('--source-color-dark') || '#3c730c';
export const processorPrimary = getComputedStyle(document.body).getPropertyValue('--processor-color') || '#f59e0b';
export const processorDark = getComputedStyle(document.body).getPropertyValue('--processor-color-dark') || '#9c6609';
export const sinkPrimary = getComputedStyle(document.body).getPropertyValue('--sink-color') || '#ec4899';
export const sinkDark = getComputedStyle(document.body).getPropertyValue('--sink-color-dark') || '#a82768';
export const streamPrimary = getComputedStyle(document.body).getPropertyValue('--stream-color') || '#0fb29a';

export type ColorVariables = typeof colorVariables;

const colorVariables = {
  brandPrimary, brandDark, brandSecondary,
  datasourcePrimary, datasourceDark,
  modelPrimary, modelDark,
  datasetPrimary, datasetDark,
  batchDark, batchPrimary,
  sourceDark, sourcePrimary,
  processorDark, processorPrimary,
  sinkDark, sinkPrimary,
  streamPrimary
};
export default colorVariables;

export const defaultFileContent = `:root {
  --alida-primary: #677ab1;
  --alida-primary-rgb: 103,122,177;
  --alida-primary-dark: #3f4e75;
  --alida-secondary: #9aa6d2;
  --alida-secondary-rgb: 154,166,210;
  --datasource-color: #2dd4bf;
  --datasource-color-dark: #0d9488;
  --datasource-color-rgb: 45, 212, 191;
  --dataset-color: #28ad7d;
  --dataset-color-dark: #19694c;
  --dataset-color-rgb: 25, 105, 76;
  --model-color: #31aee4;
  --model-color-dark: #1c7196;
  --model-color-rgb: 49, 174, 228;
  --batch-color: #64748b;
  --batch-color-dark: #3b4553;
  --stream-color: #0fb29a;
  --source-color: #77ce26;
  --source-color-dark: #3c730c;
  --processor-color: #f59e0b;
  --processor-color-dark: #9c6609;
  --sink-color: #ec4899;
  --sink-color-dark: #a82768;
}`;

const uiColorsMap: Record<string, keyof ColorVariables> = {
  "alida-primary": "brandPrimary",
  "alida-secondary": "brandSecondary",
  "alida-primary-dark": "brandDark",
  "datasource-color": "datasourcePrimary",
  "datasource-color-dark": "datasourceDark",
  "model-color": "modelPrimary",
  "model-color-dark": "modelDark",
  "dataset-color": "datasetPrimary",
  "dataset-color-dark": "datasetDark",
  "batch-color": "batchPrimary",
  "batch-color-dark": "batchDark",
  "source-color": "sourcePrimary",
  "source-color-dark": "sourceDark",
  "processor-color": "processorPrimary",
  "processor-color-dark": "processorDark",
  "sink-color": "sinkPrimary",
  "sink-color-dark": "sinkDark",
  "stream-color": "streamPrimary",
}

export const readColorVariablesFromCssString = (fileContent: string) => {
  const css = fileContent;

  const regex = /--([\w-]+):\s*([^;]+);/g;

  const styles: Record<string, string> = {};
  let match;

  while ((match = regex.exec(css)) !== null) {
    const [, key, value] = match;
    if (!key.endsWith("-rgb"))
      styles[uiColorsMap[key]] = value.trim();
  }

  return styles;
}

export const defaultNavigationSections: Record<SectionKey, NavigationSection> = {
  'dashboard': {
    labels: { title: 'Dashboard' },
    url: '/dashboard',
  },
  'data-management': {
    labels: { title: 'Identify' },
    items: ['datasources', 'datasets', 'persona-analyzer', 'models'],
  },
  'datasources': {
    labels: { title: 'Data Sources', singular: 'Data Source', plural: 'Data Sources' },
    url: '/datasources',
    subSection: true,
  },
  'persona-analyzer': {
    labels: { title: 'Persona Analyser' },
    url: window.__env.PERSONA_ANALYZER_EXTERNAL_URL || 'https://persona.ilabhub.atc.gr/',
    subSection: true,
    external: true,
  },
  'spiderisk': {
    labels: { title: 'Spiderisk' },
    url: window.__env.SPIDERISK_EXTERNAL_URL || 'https://themis.it-innovation.soton.ac.uk/system-modeller/',
    subSection: true,
    external: true,
  },
  'datasets': {
    labels: { title: 'Datasets', singular: 'Dataset', plural: 'Datasets' },
    url: '/datasets',
    subSection: true,
  },
  'models': {
    labels: { title: 'Models', singular: 'Model', plural: 'Models' },
    url: '/models',
    subSection: true,
  },
  'assets-management': {
    labels: { title: 'Assess' },
    items: ['projects', 'spiderisk'],
  },
  'services': {
    labels: { title: 'Services', singular: 'Service', plural: 'Services' },
    url: '/services',
    subSection: true,
  },
  'applications': {
    labels: { title: 'BDA Applications', singular: 'Application', plural: 'Applications' },
    url: '/bda-applications',
    subSection: true,
  },
  'projects': {
    labels: { title: 'Assessments', singular: 'Assessment', plural: 'Assessments' },
    url: '/projects',
    subSection: true,
  },
  'llm': {
    labels: { title: 'LLM' },
    items: ['rags', 'loaders', 'llm-models'],
  },
  'rags': {
    labels: { title: 'Rags', singular: 'Rag', plural: 'Rags' },
    url: '/llm/rags',
    subSection: true,
  },
  'loaders': {
    labels: { title: 'Loaders', singular: 'Loader', plural: 'Loaders' },
    url: '/llm/loaders',
    subSection: true,
  },
  'llm-models': {
    labels: { title: 'Models', singular: 'Model', plural: 'Models' },
    url: '/llm/models',
    subSection: true,
  },
  'api-keys': {
    labels: { title: 'API Keys', singular: 'API Key', plural: 'API Keys' },
    url: '/api-keys',
    hide: true,
  },
  'environment-variables': {
    labels: { title: 'Environment Variables', singular: 'Environment variable', plural: 'Environment variables' },
    url: '/environment-variables',
    hide: true,
  },
  'enhance': {
    labels: { title: 'Enhance' },
    url: window.__env.TAIME_EXTERNAL_URL || 'https://github.com/Dieguli/TAIME',
    external: true,
  },
  'control-panel': {
    admin: true,
    labels: { title: 'Control Panel' },
    url: '/control-panel',
  },
  'dataspace-connectors': {
    labels: { title: 'Data Space Connectors', singular: 'Data Space Connector', plural: 'Data Space Connectors' },
    url: '/dataspace-connectors',
  },
};